import * as React from 'react';
import { useObservable } from 'react-use-observable';

import { App, Button, Table, Typography } from 'antd';

import { CHECKOUT_ELEMENTS_URL } from 'settings';
import { BaseOfferTag } from 'v2/components/Shared/BaseOfferTag';
import { TableActionButton } from 'v2/components/Shared/TableActionButton';
import Truncate from 'v2/components/Shared/TruncateText';
import { useProductOffers } from 'v2/hooks/useProductOffers';
import type { ProductOffer } from 'v2/interfaces/product';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import { Icon } from '@eduzz/ui-antd-theme';

type ListProps = {
  isAffiliate: boolean;
  productId: number;
  handleLinksClick: (offer: ProductOffer) => void;
};

function Component({ isAffiliate, productId, handleLinksClick }: ListProps) {
  const { message } = App.useApp();
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const offersQuery = useProductOffers(
    {
      productId
    },
    { enabled: !!productId }
  );

  function getBaseOffer(offer: ProductOffer) {
    return offer.prices?.find(price => price.currency.code === 'BRL');
  }

  function formatPriceBasedOnBaseOffer(offer: ProductOffer) {
    const baseOffer = getBaseOffer(offer);
    if (!baseOffer) return 'R$ 0,00';
    return baseOffer.value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  function mountOfferLink(offer: ProductOffer) {
    const url = new URL(`${CHECKOUT_ELEMENTS_URL}/${offer.key}`);

    if (isAffiliate && user) {
      url.searchParams.append('a', String(user.id));
    }

    return String(url);
  }

  async function copyLinkToClipboard(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      message.success('Link copiado para a área de transferência');
    } catch {
      message.error('Não foi possível copiar o link');
    }
  }

  const columns = [
    {
      title: 'Nome da oferta',
      key: 'name',
      render: (_: any, offer: ProductOffer) => (
        <div className='flex items-center gap-2 whitespace-nowrap'>
          <Typography.Text>
            <Truncate count={30}>{offer.name}</Truncate>
          </Typography.Text>
          {offer.is_default && <BaseOfferTag />}
        </div>
      )
    },
    {
      title: 'Valor base',
      key: 'value',
      width: 112,
      render: (_: any, offer: ProductOffer) => <Typography.Text>{formatPriceBasedOnBaseOffer(offer)}</Typography.Text>
    },
    {
      title: 'Preços',
      key: 'prices',
      width: 60,
      align: 'center' as const,
      render: (_: any, offer: ProductOffer) => (
        <Typography.Text>{String(offer.prices?.length || 0).padStart(2, '0')}</Typography.Text>
      )
    },
    {
      title: 'Página de pagamento (Eduzz Checkout Elements)',
      key: 'link',
      render: (_: any, offer: ProductOffer, index: number) => (
        <div className='flex items-center gap-2'>
          <Icon.Link size={16} />
          <Typography.Link href={mountOfferLink(offer)} target='_blank' rel='noreferrer' className='min-w-56'>
            {mountOfferLink(offer)}
          </Typography.Link>
          <Button
            type='text'
            id={`offer-copy-link-${index}`}
            onClick={() => copyLinkToClipboard(mountOfferLink(offer))}
            icon={<Icon.Copy size={16} />}
          />
        </div>
      )
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      align: 'center' as const,
      render: (_: any, offer: ProductOffer) => (
        <TableActionButton ariaLabel='Ver links desta oferta' onClick={() => handleLinksClick(offer)} />
      )
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={offersQuery.data ?? []}
      rowKey='id'
      pagination={false}
      size='small'
      sticky
      scroll={{ x: 'max-content' }}
      loading={offersQuery.isLoading}
      locale={{
        emptyText: offersQuery.isError
          ? 'Ocorreu um erro ao carregar as ofertas'
          : 'Nenhuma oferta cadastrada para este produto'
      }}
    />
  );
}

const List = React.memo(Component);

export { List };
